import * as React from 'react';
import { useReducedMotion } from 'framer-motion';
import { cn } from '@/lib/utils';

// ─────────────────────────────────────────────────────────────────────────
// HandwritingPathReveal — true per-stroke SVG handwriting animation.
//
// Each glyph's outline path is animated via stroke-dasharray (the stroke draws
// itself along the path), then the fill fades in once the stroke is mostly
// complete. The result reads as "writing then inking" — the dramatic wow
// moment the letter-stagger primitive can't quite hit.
//
// Reserve this for ONE moment per session (the home greeting Builder name).
// The glyph JSON is lazy-loaded so other surfaces don't pay for it.
//
// Glyph data is pre-extracted from the Caveat 600 font via
// scripts/extract-handwriting-path.mjs and stored in caveat-glyphs.json.
// Future: pre-bake per-Builder paths server-side on signup, drop the JSON.
// ─────────────────────────────────────────────────────────────────────────

type GlyphMap = { [char: string]: { d: string; advance: number } };

interface GlyphData {
  family: string;
  weight: number;
  fontSize: number;
  ascender: number;
  descender: number;
  lineHeight: number;
  glyphs: GlyphMap;
}

// Lazy chunk: only loaded when this component renders.
let glyphsPromise: Promise<GlyphData> | null = null;
function loadGlyphs(): Promise<GlyphData> {
  if (!glyphsPromise) {
    glyphsPromise = import('./caveat-glyphs.json').then((m) => m.default as GlyphData);
  }
  return glyphsPromise;
}

export type HandwritingTone = 'ink' | 'race' | 'teal' | 'coral';

export interface HandwritingPathRevealProps
  extends Omit<React.HTMLAttributes<HTMLSpanElement>, 'children'> {
  /** The text to draw (e.g. a Builder name). */
  text: string;
  /** Visual size in pixels — matches font-size. */
  fontSize?: number;
  /** Total animation duration across the whole word, in seconds. */
  duration?: number;
  /** Delay before the first letter begins drawing. */
  delay?: number;
  /** Stroke width during the drawing phase. Default: ~2px scaled to fontSize. */
  strokeWidth?: number;
  /** Color tone — pulled from Foundry tokens. */
  tone?: HandwritingTone;
}

const TONE_VAR: Record<HandwritingTone, string> = {
  ink: 'var(--fd-text)',
  race: 'var(--fd-accent)',
  teal: 'var(--fd-teal)',
  coral: 'var(--fd-coral)',
};

export const HandwritingPathReveal = React.forwardRef<HTMLSpanElement, HandwritingPathRevealProps>(
  (
    {
      text,
      fontSize = 96,
      duration = 1.6,
      delay = 0.2,
      strokeWidth,
      tone = 'ink',
      className,
      style,
      ...rest
    },
    ref,
  ) => {
    const prefersReducedMotion = useReducedMotion();
    const [glyphData, setGlyphData] = React.useState<GlyphData | null>(null);
    const [revealed, setRevealed] = React.useState(false);

    // Lazy-load glyph JSON on mount.
    React.useEffect(() => {
      let cancelled = false;
      loadGlyphs().then((data) => {
        if (!cancelled) setGlyphData(data);
      });
      return () => {
        cancelled = true;
      };
    }, []);

    // Trigger draw after glyphs are loaded + delay elapsed.
    React.useEffect(() => {
      if (!glyphData || prefersReducedMotion) return;
      let rafId = 0;
      const id = window.setTimeout(() => {
        rafId = window.requestAnimationFrame(() => setRevealed(true));
      }, Math.max(0, delay * 1000));
      return () => {
        window.clearTimeout(id);
        if (rafId) window.cancelAnimationFrame(rafId);
      };
    }, [glyphData, delay, prefersReducedMotion]);

    // Until glyphs load, render the text statically in the Caveat font as a
    // graceful fallback. Once glyphs arrive, the SVG renders on top.
    if (!glyphData) {
      return (
        <span
          ref={ref}
          className={cn('inline-block font-caveat font-medium leading-none', className)}
          style={{ fontSize, color: TONE_VAR[tone], ...style }}
          {...rest}
        >
          {text}
          <span className="sr-only">{text}</span>
        </span>
      );
    }

    const scale = fontSize / glyphData.fontSize;

    const chars = Array.from(text);
    let cumX = 0;
    const placed = chars.map((char) => {
      const g = glyphData.glyphs[char];
      if (!g) {
        cumX += glyphData.fontSize * 0.4;
        return null;
      }
      const x = cumX;
      cumX += g.advance;
      return { char, x, d: g.d };
    });

    const padX = 6;
    const totalRefWidth = cumX + padX * 2;
    const totalRefHeight = glyphData.lineHeight + padX * 2;
    const baselineY = glyphData.ascender + padX / 2;

    const visibleChars = placed.filter((p): p is NonNullable<typeof p> => p !== null);
    const stagger = (duration * 0.55) / Math.max(1, visibleChars.length);
    const strokeDur = Math.max(0.32, duration * 0.32);
    const fillDur = Math.max(0.2, duration * 0.18);
    const sw = strokeWidth ?? Math.max(1.5, fontSize * 0.022);

    const reduced = prefersReducedMotion;

    return (
      <span
        ref={ref}
        className={cn('inline-block leading-none', className)}
        style={{ fontSize, color: TONE_VAR[tone], ...style }}
        {...rest}
      >
        <svg
          viewBox={`0 0 ${totalRefWidth} ${totalRefHeight}`}
          width={totalRefWidth * scale}
          height={totalRefHeight * scale}
          xmlns="http://www.w3.org/2000/svg"
          aria-hidden
          style={{ display: 'block', overflow: 'visible' }}
        >
          <g transform={`translate(${padX}, 0)`}>
            {placed.map((gly, i) => {
              if (!gly) return null;
              const letterDelay = i * stagger;
              const fillStart = letterDelay + strokeDur * 0.65;

              return (
                <path
                  key={`${gly.char}-${i}`}
                  d={gly.d}
                  transform={`translate(${gly.x}, ${baselineY})`}
                  fill={TONE_VAR[tone]}
                  stroke={TONE_VAR[tone]}
                  strokeWidth={sw}
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  pathLength={1}
                  style={{
                    fillOpacity: reduced || revealed ? 1 : 0,
                    strokeOpacity: reduced ? 1 : 0.95,
                    strokeDasharray: 1,
                    strokeDashoffset: reduced || revealed ? 0 : 1,
                    transition: reduced
                      ? 'none'
                      : `stroke-dashoffset ${strokeDur}s cubic-bezier(0.55, 0.05, 0.45, 0.95) ${letterDelay}s, fill-opacity ${fillDur}s ease-out ${fillStart}s`,
                  }}
                />
              );
            })}
          </g>
        </svg>
        <span className="sr-only">{text}</span>
      </span>
    );
  },
);
HandwritingPathReveal.displayName = 'HandwritingPathReveal';
